import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

export const cleanString = (val) => {
  if (typeof val !== 'string') return val;
  let result = val.trim();

  let prev;
  do {
    prev = result;
    result = result.trim();

    // Check if it's wrapped in double quotes
    if (result.startsWith('"') && result.endsWith('"')) {
      result = result.slice(1, -1).trim();
    }
    // Check if it starts with quote and ends with quote + comma
    else if (result.startsWith('"') && result.endsWith('",')) {
      result = result.slice(1, -2).trim();
    }
    // Check if it starts with quote and ends with quote + period
    else if (result.startsWith('"') && result.endsWith('".')) {
      result = result.slice(1, -2).trim() + '.';
    }
    // Check trailing comma
    else if (result.endsWith(',')) {
      result = result.slice(0, -1).trim();
    }
    // Check trailing quote without leading quote
    else if (result.endsWith('"')) {
      result = result.slice(0, -1).trim();
    }
    // Check leading quote without trailing quote
    else if (result.startsWith('"')) {
      result = result.slice(1).trim();
    }
  } while (result !== prev);

  return result;
};

export const sanitizeGame = (game) => {
  if (!game || typeof game !== 'object') return game;
  const cleaned = { ...game };
  if (cleaned.description) cleaned.description = cleanString(cleaned.description);
  if (cleaned.synopsis) cleaned.synopsis = cleanString(cleaned.synopsis);
  if (cleaned.tagline) cleaned.tagline = cleanString(cleaned.tagline);
  if (cleaned.title) cleaned.title = cleanString(cleaned.title);

  if (cleaned.platforms && typeof cleaned.platforms === 'object') {
    const cleanedPlatforms = {};
    for (const [platformKey, platformInfo] of Object.entries(cleaned.platforms)) {
      if (platformInfo && typeof platformInfo === 'object') {
        const cleanedInfo = { ...platformInfo };
        if (cleanedInfo.system_requirements && typeof cleanedInfo.system_requirements === 'object') {
          const cleanedReqs = {};
          for (const [reqKey, reqVal] of Object.entries(cleanedInfo.system_requirements)) {
            if (typeof reqVal === 'string') {
              cleanedReqs[reqKey] = cleanString(reqVal);
            } else {
              cleanedReqs[reqKey] = reqVal;
            }
          }
          cleanedInfo.system_requirements = cleanedReqs;
        }
        cleanedPlatforms[platformKey] = cleanedInfo;
      } else {
        cleanedPlatforms[platformKey] = platformInfo;
      }
    }
    cleaned.platforms = cleanedPlatforms;
  }
  return cleaned;
};

const initialState = {
  games: [],
  deletedGames: [],
  searchGames: null,
  selectedGame: null, // Single game with isPurchased
  platformWiseGames: {},
  loading: false,
  headerSearchQuery: '',
  searchQuery: '',
  selectedCategory: 'All',
  activeTab: 'Trending',
  selectedGameId: null,
  lastGamesPayload: null,
  currentLoadingPayload: null,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 9
  },
  statusCounts: {
    total: 0,
    active: 0,
    draft: 0,
    inactive: 0
  }
};

// Async Thunk: Fetch Single Game by ID (with isPurchased)
export const fetchGameById = createAsyncThunk(
  'game/fetchGameById',
  async (gameId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/games/${gameId}`);
      if (res.data.success) {
        const g = res.data.game;
        return sanitizeGame({
          ...g,
          id: g._id || g.id,
          isPurchased: g.isPurchased || false
        });
      }
      return rejectWithValue('Failed to fetch game');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Fetch Games grouped by Platform
export const fetchGamesPlatformWise = createAsyncThunk(
  'game/fetchGamesPlatformWise',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/games/platform-wise');
      if (res.data.success) {
        // Sanitize games in each platform group
        const sanitizedData = {};
        for (const [platform, value] of Object.entries(res.data.data)) {
          sanitizedData[platform] = {
            count: value.count,
            games: (value.games || []).map(g => sanitizeGame({ ...g, id: g._id || g.id }))
          };
        }
        return sanitizedData;
      }
      return rejectWithValue('Failed to fetch platform-wise games');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Add Game
export const addGame = createAsyncThunk(
  'game/addGame',
  async ({ formData, onProgress }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/games', formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total
            );
            onProgress(percentCompleted);
          }
        },
      });
      if (res.data.success) {
        return sanitizeGame(res.data.game);
      }
      return rejectWithValue({ message: 'Failed to add game' });
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Async Thunk: Update Game
export const updateGame = createAsyncThunk(
  'game/updateGame',
  async ({ id, formData, onProgress }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/games/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total
            );
            onProgress(percentCompleted);
          }
        },
      });
      if (res.data.success) {
        return sanitizeGame(res.data.game);
      }
      return rejectWithValue({ message: 'Failed to update game' });
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Async Thunk: Delete Game
export const deleteGame = createAsyncThunk(
  'game/deleteGame',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/games/${id}`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue({ message: 'Failed to delete game' });
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Async Thunk: Fetch Games with Payload-Based Caching
export const fetchGames = createAsyncThunk(
  'game/fetchGames',
  async (payload = {}, { rejectWithValue }) => {
    try {
      const {
        search,
        searchOnlyName,
        category,
        categories,
        tag,
        status,
        minPrice,
        maxPrice,
        minRating,
        platforms,
        sortBy,
        page = 1,
        limit = 12,
        isAdmin,
        createdBy,
        date
      } = payload;

      const params = { page, limit };
      if (search) params.search = search;
      if (searchOnlyName) params.searchOnlyName = searchOnlyName;
      if (category && category !== 'All') params.category = category;
      if (categories) params.categories = Array.isArray(categories) ? categories.join(',') : categories;
      if (tag && tag !== 'All') params.tag = tag;
      if (status) params.status = status;
      if (minPrice !== undefined) params.minPrice = minPrice;
      if (maxPrice !== undefined) params.maxPrice = maxPrice;
      if (minRating !== undefined) params.minRating = minRating;
      if (platforms) params.platforms = Array.isArray(platforms) ? platforms.join(',') : platforms;
      if (sortBy) params.sortBy = sortBy;
      if (isAdmin) params.isAdmin = isAdmin;
      if (createdBy && createdBy !== 'All') params.createdBy = createdBy;
      if (date) params.date = date;

      const res = await axiosInstance.get('/games', { params });
      if (res.data.success) {
        return {
          games: res.data.games.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })),
          pagination: res.data.pagination || {
            currentPage: page,
            totalPages: 1,
            totalItems: res.data.games.length,
            limit
          },
          statusCounts: res.data.statusCounts || {
            total: 0,
            active: 0,
            draft: 0,
            inactive: 0
          },
          payload
        };
      }
      return rejectWithValue('Failed to fetch games');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    condition: (payload = {}, { getState }) => {
      const { game } = getState();
      const hasGames = game.games && game.games.length > 0;

      // Compare current payload with last payload
      const isPayloadEqual = JSON.stringify(game.lastGamesPayload) === JSON.stringify(payload);

      if (hasGames && isPayloadEqual) {
        return false; // Skip the api request
      }

      // If we are currently loading this exact same request, skip
      if (game.loading && JSON.stringify(game.currentLoadingPayload) === JSON.stringify(payload)) {
        console.log('[RTK Pending Hit] Games payload is already loading. Skipping duplicate API call.', payload);
        return false;
      }
    }
  }
);

// Async Thunk: Fetch search autocomplete games
export const fetchSearchGames = createAsyncThunk(
  'game/fetchSearchGames',
  async (payload = {}, { rejectWithValue }) => {
    try {
      const { search } = payload;
      const res = await axiosInstance.get('/games/search-autocomplete', { params: { search } });
      if (res.data.success) {
        return res.data.games;
      }
      return rejectWithValue('Failed to fetch search suggestions');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Fetch soft-deleted games
export const fetchDeletedGames = createAsyncThunk(
  'game/fetchDeletedGames',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/games/deleted');
      if (res.data.success) {
        return res.data.games || [];
      }
      return rejectWithValue('Failed to fetch deleted games');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Restore soft-deleted game
export const restoreGame = createAsyncThunk(
  'game/restoreGame',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.patch(`/games/${id}/restore`, {});
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to restore game');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Purge game permanently
export const purgeGame = createAsyncThunk(
  'game/purgeGame',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/games/${id}/permanent`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to permanently delete game');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Fetch game download link
export const fetchDownloadLink = createAsyncThunk(
  'game/fetchDownloadLink',
  async ({ gameId, platform }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/games/${gameId}/download-link?platform=${platform}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async Thunk: Fetch more games for platform
export const fetchMorePlatformGames = createAsyncThunk(
  'game/fetchMorePlatformGames',
  async ({ platform, page, limit = 12 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/games', {
        params: {
          platforms: platform,
          page,
          limit
        }
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setHeaderSearchQuery: (state, action) => {
      state.headerSearchQuery = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSelectedGameId: (state, action) => {
      state.selectedGameId = action.payload;
    },
    setSearchGames: (state, action) => {
      state.searchGames = action.payload;
    },
    updateGameInStore: (state, action) => {
      const sanitized = sanitizeGame(action.payload);
      const index = state.games.findIndex(g => (g._id || g.id) === (sanitized._id || sanitized.id));
      if (index !== -1) {
        state.games[index] = { ...sanitized, id: sanitized._id || sanitized.id };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGames.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentLoadingPayload = action.meta.arg;
      })
      .addCase(fetchGames.fulfilled, (state, action) => {
        if (action.payload.pagination && action.payload.pagination.currentPage > 1 && !action.payload.payload?.isAdmin) {
          state.games = [...state.games, ...action.payload.games];
        } else {
          state.games = action.payload.games;
        }
        state.pagination = action.payload.pagination;
        if (action.payload.statusCounts) {
          state.statusCounts = action.payload.statusCounts;
        }
        state.lastGamesPayload = action.payload.payload;
        state.currentLoadingPayload = null;
        state.loading = false;
      })
      .addCase(fetchGames.rejected, (state, action) => {
        state.loading = false;
        state.currentLoadingPayload = null;
        state.error = action.payload;
      })
      .addCase(fetchGameById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGameById.fulfilled, (state, action) => {
        state.selectedGame = action.payload;
        state.loading = false;
      })
      .addCase(fetchGameById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addGame.fulfilled, (state, action) => {
        state.games.unshift({ ...action.payload, id: action.payload._id || action.payload.id });
      })
      .addCase(updateGame.fulfilled, (state, action) => {
        const index = state.games.findIndex(g => (g._id || g.id) === (action.payload._id || action.payload.id));
        if (index !== -1) {
          state.games[index] = { ...action.payload, id: action.payload._id || action.payload.id };
        }
      })
      .addCase(deleteGame.fulfilled, (state, action) => {
        state.games = state.games.filter(g => (g._id || g.id) !== action.payload);
      })
      .addCase(fetchGamesPlatformWise.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGamesPlatformWise.fulfilled, (state, action) => {
        state.platformWiseGames = action.payload;
        state.loading = false;
      })
      .addCase(fetchGamesPlatformWise.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDeletedGames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeletedGames.fulfilled, (state, action) => {
        state.deletedGames = action.payload;
        state.loading = false;
      })
      .addCase(fetchDeletedGames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(restoreGame.fulfilled, (state, action) => {
        state.deletedGames = state.deletedGames.filter(g => (g._id || g.id) !== action.payload);
      })
      .addCase(purgeGame.fulfilled, (state, action) => {
        state.deletedGames = state.deletedGames.filter(g => (g._id || g.id) !== action.payload);
      })
      .addCase(fetchSearchGames.fulfilled, (state, action) => {
        state.searchGames = action.payload;
      })
      .addCase(fetchSearchGames.rejected, (state) => {
        state.searchGames = [];
      });
  }
});

export const {
  setHeaderSearchQuery,
  setSearchQuery,
  setSelectedCategory,
  setActiveTab,
  setSelectedGameId,
  setSearchGames,
  updateGameInStore,
} = gameSlice.actions;

export default gameSlice.reducer;
